import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Tooltip,
} from '@mui/material';
import { AppIconButton } from '../../../components/design-system';
import { ContentCopy as CopyIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useAppToolbar } from '../../../contexts/AppToolbarContext';
import { useFormatting } from '../../../contexts/FormattingContext';
import { type EncryptionKey } from '../../../services/EncryptionKeysService';
import { providerEncryptionKeysService } from '../../../services/ProviderEncryptionKeysService';
import { usePaginationSize } from '../../../hooks/useAppConfig';
import { EnhancedTable, type TableColumn, type FilterConfig } from '../../../components/ui/EnhancedTable';

export default function EncryptionKeys() {
  const { formatDate: formatDateService } = useFormatting();
  const defaultPaginationSize = usePaginationSize();
  const [keys, setKeys] = useState<EncryptionKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPaginationSize);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);

  useEffect(() => {
    loadKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadKeys reads latest filter state
  }, [page, rowsPerPage, filters.status, refreshTrigger]);

  const loadKeys = async () => {
    try {
      setLoading(true);
      const response = await providerEncryptionKeysService.listEncryptionKeys({
        status: filters.status || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setKeys(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      console.error('Failed to load encryption keys:', err);
      setKeys([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return formatDateService(new Date(dateString));
  };

  const handleCopyArn = async (arn: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(arn);
      setCopiedKeyId(keyId);
      setTimeout(() => setCopiedKeyId(null), 2000);
    } catch (err) {
      console.error('Failed to copy ARN:', err);
    }
  };

  const getStatusColor = (status: string): 'success' | 'default' | 'error' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DEPRECATED':
        return 'default';
      case 'REVOKED':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns: TableColumn<EncryptionKey>[] = [
    {
      field: 'tenant',
      headerName: 'Tenant',
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueGetter: (row) => row.tenant?.name || 'Provider',
      renderCell: (row) =>
        row.tenant?.name || (
          <Chip label="Provider" size="small" color="primary" variant="outlined" />
        ),
    },
    {
      field: 'keyType',
      headerName: 'Key Type',
      width: 150,
      sortable: true,
    },
    {
      field: 'kmsKeyId',
      headerName: 'KMS Key ARN',
      minWidth: 300,
      flex: 1,
      sortable: false,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
            title={row.kmsKeyId}
          >
            {row.kmsKeyId}
          </Typography>
          <Tooltip title={copiedKeyId === row.keyId ? 'Copied!' : 'Copy ARN'}>
            <AppIconButton
              size="small"
              onClick={() => handleCopyArn(row.kmsKeyId, row.keyId)}
              sx={{
                padding: '4px',
                color: copiedKeyId === row.keyId ? 'success.main' : 'action.active',
              }}
            >
              <CopyIcon fontSize="small" />
            </AppIconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      align: 'center',
      sortable: true,
      filterable: true,
      renderCell: (row) => (
        <Chip label={row.status} color={getStatusColor(row.status)} size="small" />
      ),
    },
    {
      field: 'activatedAt',
      headerName: 'Activated',
      width: 150,
      sortable: true,
      renderCell: (row) => (
        <Typography variant="body2">{formatDate(row.activatedAt)}</Typography>
      ),
    },
  ];

  const filterOptions: FilterConfig[] = [
    {
      field: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'ACTIVE', label: 'Active' },
        { value: 'DEPRECATED', label: 'Deprecated' },
        { value: 'REVOKED', label: 'Revoked' },
      ],
    },
  ];

  const handleExport = () => {
    const csvData = keys.map((key) => ({
      Tenant: key.tenant?.name || 'Provider',
      'Key Type': key.keyType,
      Status: key.status,
      'KMS ARN': key.kmsKeyId,
      Activated: formatDate(key.activatedAt),
    }));

    const headers = Object.keys(csvData[0] ?? {});
    const csv = [
      headers.join(','),
      ...csvData.map((row) =>
        headers.map((header) => `"${row[header as keyof typeof row]}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `encryption-keys-${new Date().toISOString()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton
        onClick={() => setRefreshTrigger((prev) => prev + 1)}
        disabled={loading}
        aria-label="Refresh"
      >
        <RefreshIcon />
      </AppIconButton>
    ),
    [loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        gap: 2,
        p: 2,
      }}
    >
      <Alert severity="info" sx={{ flexShrink: 0, borderRadius: 2 }}>
        <Typography variant="body2" fontWeight="600">
          AWS KMS automatic key rotation
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          CMK backing material rotates annually via AWS KMS. This registry is read-only — no
          application-managed rotation or data re-encryption jobs.
        </Typography>
      </Alert>

      <EnhancedTable
        data={keys}
        totalCount={total}
        loading={loading}
        columns={columns}
        getRowId={(row) => row.id}
        page={page + 1}
        pageSize={rowsPerPage}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={(newPage) => setPage(newPage - 1)}
        onPageSizeChange={setRowsPerPage}
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={(filters) => setFilters(filters as Record<string, string>)}
        exportable
        onExport={handleExport}
        emptyStateTitle="No Encryption Keys Found"
        emptyStateDescription="No encryption keys match your search criteria"
        rowHeight={56}
        stickyHeader
        maxHeight="100%"
      />

      <Alert severity="info" sx={{ flexShrink: 0, borderRadius: 2 }}>
        <Typography variant="body2" fontWeight="600" gutterBottom>
          Key statuses
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <li><Typography variant="body2" component="span"><strong>ACTIVE</strong> — encrypts and decrypts field data</Typography></li>
          <li><Typography variant="body2" component="span"><strong>DEPRECATED</strong> — decrypt-only during key rotation</Typography></li>
          <li><Typography variant="body2" component="span"><strong>REVOKED</strong> — disabled; existing data may be undecryptable</Typography></li>
        </Box>
      </Alert>
    </Box>
  );
}
